import {
    ShopperBaskets,
    ShopperCustomers,
    ShopperLogin,
    ShopperOrders,
    ShopperProducts,
    ShopperPromotions,
    ShopperSearch,
} from 'commerce-sdk-isomorphic';
import Auth from '~/features/commerce-api/auth';
import { SalesforcePromotionCountdown } from '~/lib/salesforce/types/promotion-countdown';
import { TPrice } from '~/shared/components/ProductCard/components/Price';

export type StatusDetails = {
    errorData: string;
};

export type OCAPIError = {
    message: string;
    type: string;
    arguments: {
        extensionPoint: string;
        statusCode: string;
        statusDetails?: StatusDetails;
        statusMessage: string;
    };
};

export type CAPIError = {
    fault: boolean;
    title: string;
    type: string;
    detail: string;
    arguments: any;
};

export type RequiredParameters = {
    [key: string]: any;
};

export type Headers = {
    [key: string]: any;
};

export type CommerceApiParams = {
    siteId: string;
    clientId: string;
    channelId: string;
    accountClientId: string;
    organizationId: string;
    shortCode: string;
    publicUrl: string;
};

export type CommerceApiConfig = {
    ocapiHost: string;
    parameters: CommerceApiParams;
    proxy: string;
};

export type CombinedCommerceApiConfig = {
    ocapiHost: string;
    parameters: CommerceApiParams;
    proxy: string;
    locale?: string;
    currency?: string;
};

export type CommerceApiCResponse = {
    detail?: string;
    title?: string;
    type?: string;
};

export type CustomerCredentials = {
    email: string;
    password: string;
};

export type Store = {
    address1: string;
    address2?: string;
    city: string;
    countryCode: string;
    distance: number;
    distanceUnit: 'km' | 'mi';
    email: string;
    id: string;
    name: string;
    phone: string;
    postal_code: string;
    postalCode?: string;
    stateCode: string;
    store_locator_enabled: boolean;
    pos_enabled: boolean;
    latitude?: number;
    longitude?: number;
} & {
    c_clickCollectFlag: boolean;
    c_clickCollectMaxPackages: number;
    c_clickReserveFlag: boolean;
    c_clickReserveEnabled: boolean;
    c_storeClosed: boolean;
    c_openingHours?: { day: string; opens: string | null; closes: string | null }[];
    c_weight: number;
    c_storeClosedReason?: string;
    c_openingStatusToday?: { day: string; isClosedToday: boolean; isClosedTodayMessage?: string };
    _type: string;
    [key: string]: any;
};

export type StoreSortingType = 'anonymous' | 'rmembers' | 'instore';

export type StoreProps = {
    count?: number; // 	The maximum number of instances per request. Default value is 25. maxIntegerValue=200, minIntegerValue=1
    countryCode?: string; // The two letter ISO country code e.g. \"US\". maxLength=2, minLength=2
    distanceUnit?: string; // The distance unit. Supported values are \"mi\" and \"km\" (for miles and kilometers respectively, default is \"km\").
    latitude?: number; // The geo coordinate latitude to search for stores (value range -90.00 .. 90.00). maxNumberValue=90.0, minNumberValue=-90.0
    longitude?: number; // The geo coordinate longitude to search for stores (value range -180.00 .. 180.00). maxNumberValue=180.0, minNumberValue=-180.0
    maxDistance?: number; // The area (radius) in distance unit where stores will be searched for (default is half of the earth's perimeter). maxNumberValue=20012.0, minNumberValue=0.0
    postalCode?: string; // The postal code e.g. \"01801\".	maxLength=10, minLength=3
    start?: number; // The result set index to return the first instance for. Default value is 0. minIntegerValue=0
    id?: string;
    pid?: string; // The product id.
    type?: StoreSortingType;
};

export type StoreList = {
    latitude: number;
    count: number;
    longitude: number;
    maxDistance?: number;
};

export type ShopperStores = {
    getStores(args: { parameters: StoreProps }): Promise<StoreResponse>;
    getStore(args: { parameters: StoreProps }): Promise<StoreResponse>;
    getStoreList(args: { parameters: StoreProps }): Promise<StoreResponse>;
};

export type ShopperLookup = {
    lookupCustomer: (args: any) => Promise<Customer>;
};

export type CommerceApiContext = {
    shopperCustomers: ShopperCustomers<CommerceApiParams> | undefined;
    shopperStores: ShopperStores | undefined;
    shopperBaskets:
        | (ShopperBaskets<CommerceApiParams> & {
              updatePaymentInstrumentOfOrder: (options: {
                  headers?: {
                      [key: string]: string;
                  };
                  body: any;
                  parameters: {
                      orderNo: string;
                      paymentInstrumentId: string;
                  };
              }) => Promise<Order>;
          })
        | undefined;
    shopperProducts: ShopperProducts<CommerceApiParams> | undefined;
    shopperPromotions: ShopperPromotions<CommerceApiParams> | undefined;
    shopperOrders:
        | (ShopperOrders<CommerceApiParams> & {
              failOrder: (options: {
                  parameters: {
                      orderNo: string;
                      c_failureReason?: string;
                  };
                  headers?: {
                      [key: string]: string;
                  };
              }) => Promise<void>;

              createOrderWithPredeterminedNumber: (options: {
                  body: {
                      basketId: string;
                  };
                  parameters: {
                      orderNo: string;
                  };
                  headers?: {
                      [key: string]: string;
                  };
              }) => Promise<Order>;
          })
        | undefined;
    shopperSearch: ShopperSearch<CommerceApiParams> | undefined;
    shopperLogin: ShopperLogin<CommerceApiParams> | undefined;
    auth: Auth | undefined;
    shopperLookup: ShopperLookup | undefined;
    shopperGiftCertificates:
        | {
              getGiftCertificate: (options: {
                  parameters: {
                      gcnumber: string;
                      gccvv?: string;
                  };
              }) => Promise<GiftCertificateBalance>;
          }
        | undefined;
};

export type IBasketContext = {
    basket: Basket | undefined;
    setBasket: (basket: Basket | undefined) => void;
};
export type ICustomerContext = {
    customer: Customer | undefined;
    setCustomer: (customer: Customer | undefined) => void;
};

export type PaymentCardSpec = {
    cardType?: string;
    checksumVerificationEnabled?: boolean;
    description?: string;
    image?: string;
    name?: string;
    numberLengths?: Array<string>;
    numberPrefixes?: Array<string>;
} & {
    [key: string]: any;
};
export type OrderAddress = {
    address1?: string;
    address2?: string;
    city?: string;
    companyName?: string;
    countryCode?: string;
    firstName?: string;
    fullName?: string;
    id?: string;
    jobTitle?: string;
    lastName?: string;
    phone?: string;
    postBox?: string;
    postalCode?: string;
    salutation?: string;
    secondName?: string;
    stateCode?: string;
    suffix?: string;
    suite?: string;
    title?: string;
} & {
    [key: string]: any;
};
export type PaymentCard = {
    cardType?: string;
    creditCardExpired?: boolean;
    creditCardToken?: string;
    expirationMonth?: number;
    expirationYear?: number;
    holder?: string;
    issueNumber?: string;
    maskedNumber?: string;
    numberLastDigits?: string;
    validFromMonth?: number;
    validFromYear?: number;
};
export type ShippingItem = {
    adjustedTax?: number;
    basePrice?: number;
    itemId?: string;
    itemText?: string;
    price?: number;
    priceAdjustments?: Array<PriceAdjustment>;
    priceAfterItemDiscount?: number;
    shipmentId?: string;
    tax?: number;
    taxBasis?: number;
    taxClassId?: string;
    taxRate?: number;
} & {
    [key: string]: any;
};
export type Discount = {
    amount?: number;
    percentage?: number;
    priceBookId?: string;
    type: string;
} & {
    [key: string]: any;
};
export type CouponItem = {
    c_endDate?: string;
    code: string;
    couponItemId?: string;
    statusCode?: string;
    valid?: boolean;
} & {
    [key: string]: any;
};
export type PriceBookIds = Array<string>;
type ShippingPromotion = {
    calloutMsg?: string;
    promotionId?: string;
    promotionName?: string;
} & {
    [key: string]: any;
};

export type ProductItemBasket = {
    productId: string;
    quantity: number;
    c_description2?: string;
};

export type ProductItem = {
    adjustedTax?: number;
    basePrice?: number;
    bonusDiscountLineItemId?: string;
    bonusProductLineItem?: boolean;
    bundledProductItems?: Array<ProductItem>;
    /** Promotions the line will be discounted by once their countdown ends. */
    c_promotionCountdown?: SalesforcePromotionCountdown[];
    c_specialPrices?: SpecialPrices;
    gift?: boolean;
    giftMessage?: string;
    inventoryId?: string;
    itemId?: string;
    itemText?: string;
    optionItems?: Array<OptionItem>;
    price?: number;
    priceAdjustments?: Array<PriceAdjustment>;
    priceAfterItemDiscount?: number;
    priceAfterOrderDiscount?: number;
    productId?: string;
    productListItem?: ProductListItemReference;
    productName?: string;
    quantity?: number;
    shipmentId?: string;
    shippingItemId?: string;
    tax?: number;
    taxBasis?: number;
    taxClassId?: string;
    taxRate?: number;
} & {
    [key: string]: any;
};
export type BasketPaymentInstrumentRequest = {
    amount?: number;
    bankRoutingNumber?: string;
    giftCertificateCode?: string;
    paymentCard?: OrderPaymentCardRequest;
    paymentMethodId?: string;
} & {
    [key: string]: any;
};
export type Taxes = {
    taxes: {} & {
        [key: string]: any;
    };
};
export type OrderPaymentInstrument = {
    amount?: number;
    authorizationStatus?: Status;
    bankRoutingNumber?: string;
    maskedGiftCertificateCode?: string;
    paymentCard?: PaymentCard;
    paymentInstrumentId?: string;
    paymentMethodId?: string;
    c_payment_data?: string;
} & {
    [key: string]: any;
};
export type BonusDiscountLineItem = {
    bonusProducts?: Array<ProductDetailsLink>;
    couponCode?: string;
    id?: string;
    maxBonusItems?: number;
    promotionId?: string;
} & {
    [key: string]: any;
};
export type PaymentMethodResult = {
    applicablePaymentMethods?: Array<PaymentMethod>;
} & {
    [key: string]: any;
};
export type Shipment = {
    adjustedMerchandizeTotalTax?: number;
    adjustedShippingTotalTax?: number;
    gift?: boolean;
    giftMessage?: string;
    merchandizeTotalTax?: number;
    productSubTotal?: number;
    productTotal?: number;
    shipmentId?: string;
    shipmentNo?: string;
    shipmentTotal?: number;
    shippingAddress?: OrderAddress;
    shippingMethod?: ShippingMethod;
    shippingStatus?: string;
    shippingTotal?: number;
    shippingTotalTax?: number;
    taxTotal?: number;
    trackingNumber?: string;
} & {
    [key: string]: any;
};
export type CustomerInfo = {
    customerId?: string;
    customerName?: string;
    customerNo?: string;
    email: string;
} & {
    [key: string]: any;
};
export type SimpleLink = {};
export type PriceAdjustment = {
    appliedDiscount?: Discount;
    couponCode?: string;
    createdBy?: string;
    creationDate?: any;
    custom?: boolean;
    itemText?: string;
    lastModified?: any;
    manual?: boolean;
    price?: number;
    priceAdjustmentId?: string;
    promotionId?: string;
    promotionLink?: string;
    reasonCode?: string;
} & {
    [key: string]: any;
};
export type ShippingMethodResult = {
    applicableShippingMethods?: Array<ShippingMethod>;
    defaultShippingMethodId?: string;
} & {
    [key: string]: any;
};
export type ProductDetailsLink = {
    productDescription?: string;
    productId: string;
    productName?: string;
    title?: string;
};
export type OptionItem = {
    adjustedTax?: number;
    basePrice?: number;
    bonusDiscountLineItemId?: string;
    bonusProductLineItem?: boolean;
    bundledProductItems?: Array<ProductItem>;
    gift?: boolean;
    giftMessage?: string;
    inventoryId?: string;
    itemId?: string;
    itemText?: string;
    optionId: string;
    optionItems?: Array<OptionItem>;
    optionValueId: string;
    price?: number;
    priceAdjustments?: Array<PriceAdjustment>;
    priceAfterItemDiscount?: number;
    priceAfterOrderDiscount?: number;
    productId?: string;
    productListItem?: ProductListItemReference;
    productName?: string;
    quantity?: number;
    shipmentId?: string;
    shippingItemId?: string;
    tax?: number;
    taxBasis?: number;
    taxClassId?: string;
    taxRate?: number;
} & {
    [key: string]: any;
};
export const DropPointTypes = {
    both: 'Begge',
    packageLocker: 'Nærboks',
    packageShop: 'Pakkeshop',
} as const;
export type DropPointType = (typeof DropPointTypes)[keyof typeof DropPointTypes];
export type ShippingMethod = {
    description?: string;
    externalShippingMethod?: string;
    id: string;
    name?: string;
    price?: number;
    shippingPromotions?: Array<ShippingPromotion>;
} & {
    c_webshipperServiceCode?: string;
    c_webshipperId?: string;
    c_image?: string;
    c_storePickupEnabled: string;
    c_carrierCode: string;
    _type: string;
    c_estimatedArrivalTime?: string;
    c_shippingType?: string;
    c_dropPointType?: DropPointType;
    [key: string]: any;
};

export type StoreResponse = {
    count: number;
    data: Store[];
    start: number;
    total: number;
} & {
    c_clickCollectFlag: boolean;
    c_clickCollectMaxPackages: number;
    c_clickReserveFlag: boolean;
    c_storeClosed: boolean;
    c_storeIP: string;
    latitude: number;
    longitude: number;
    name: string;
    [key: string]: any;
};
export type Basket = {
    adjustedMerchandizeTotalTax?: number;
    adjustedShippingTotalTax?: number;
    agentBasket?: boolean;
    basketId?: string;
    billingAddress?: OrderAddress;
    bonusDiscountLineItems?: Array<BonusDiscountLineItem>;
    channelType?: string;
    couponItems?: Array<CouponItem>;
    creationDate?: any;
    currency?: string;
    customerInfo?: CustomerInfo;
    giftCertificateItems?: Array<GiftCertificateItem>;
    inventoryReservationExpiry?: any;
    lastModified?: any;
    merchandizeTotalTax?: number;
    notes?: SimpleLink;
    orderPriceAdjustments?: Array<PriceAdjustment>;
    orderNo?: string;
    productItems?: Array<ProductItem>;
    productSubTotal?: number;
    productTotal?: number;
    shipments?: Array<Shipment>;
    shippingItems?: Array<ShippingItem>;
    shippingTotal?: number;
    shippingTotalTax?: number;
    sourceCode?: string;
    taxTotal?: number;
    taxation?: string;
    c_memberPrice?: string;
    c_totalBonusPrice?: string;
    c_adultPurchase?: boolean;
    c_freeShippingAllowed?: boolean;
    /**
     * The basket's prices no longer match a promotion countdown - one ended since the basket was
     * last calculated, or one was set while its discount was already applied.
     */
    c_promotionCountdownRecalculationNeeded?: boolean;
} & {
    [key: string]: any;
} & CustomBasketOrderData;

export type GiftCertificateItem = {
    amount: number;
    giftCertificateItemId?: string;
    message?: string;
    recipientEmail: string;
    recipientName?: string;
    senderName?: string;
    shipmentId?: string;
} & {
    [key: string]: any;
};
export type ProductListLink = {
    description?: string;
    name?: string;
    public?: boolean;
    title?: string;
    type?: string;
} & {
    [key: string]: any;
};

export type SuggestedStore = {
    storeId: string;
    storeName: string;
    address1: string;
    address2?: string;
    city: string;
    postalCode: string;
    countryCode: string;
    phone: string;
};

export type CustomBasketOrderData = {
    c_suggestedStore?: SuggestedStore | null;
    c_subTotalRetailPrices?: number;
    c_totalDiscountExclShipping?: number;
    c_remainingTotal?: number;
    c_totalMemberDiscount?: number;
    orderTotal?: number;
    paymentInstruments?: Array<OrderPaymentInstrument>;
    c_transactionId?: string;
    c_paymentId?: string;
};

type OrderFlashStatus = {
    _type: string;
    type: string;
    message: string;
    path: string;
    details?: Status;
};

export type Order = {
    adjustedMerchandizeTotalTax?: number;
    adjustedShippingTotalTax?: number;
    billingAddress?: OrderAddress;
    bonusDiscountLineItems?: Array<BonusDiscountLineItem>;
    channelType?: string;
    confirmationStatus?: string;
    couponItems?: Array<CouponItem>;
    createdBy?: string;
    creationDate?: any;
    currency?: string;
    customerInfo?: CustomerInfo;
    customerName?: string;
    exportStatus?: string;
    externalOrderStatus?: string;
    giftCertificateItems?: Array<GiftCertificateItem>;
    globalPartyId?: string;
    lastModified?: any;
    merchandizeTotalTax?: number;
    notes?: SimpleLink;
    orderNo?: string;
    orderPriceAdjustments?: Array<PriceAdjustment>;
    orderToken?: string;
    paymentStatus?: string;
    productItems?: Array<ProductItem>;
    productSubTotal?: number;
    productTotal?: number;
    shipments?: Array<Shipment>;
    shippingItems?: Array<ShippingItem>;
    shippingStatus?: string;
    shippingTotal?: number;
    shippingTotalTax?: number;
    siteId?: string;
    sourceCode?: string;
    status?: string;
    taxTotal?: number;
    taxation?: string;
} & {
    c_redirectURL?: string;
    c_pickupStoreID?: string;
    c_paymentData: PaymentData;
    c_netsTerminalURL?: string;
    _flash?: OrderFlashStatus[];
} & CustomBasketOrderData;

type PaymentData = {
    paymentMethod?: string;
};

export type OrderPaymentCardRequest = {
    cardType?: string;
    creditCardToken?: string;
    expirationMonth?: number;
    expirationYear?: number;
    holder?: string;
    issueNumber?: string;
    maskedNumber?: string;
    validFromMonth?: number;
    validFromYear?: number;
};
export type PaymentMethod = {
    cards?: Array<PaymentCardSpec>;
    description?: string;
    id: string;
    image?: string;
    name?: string;
    paymentProcessorId?: string;
} & {
    [key: string]: any;
};
export type TaxItem = {
    id: string;
    rate: number;
    value?: number;
};
export type Status = {
    code?: string;
    message?: string;
    status?: number;
};
export type ProductListItemReference = {
    id: string;
    priority?: number;
    productDetailsLink?: ProductDetailsLink;
    productList?: ProductListLink;
    public?: boolean;
    purchasedQuantity?: number;
    quantity?: number;
    type?: string;
};
/**
 * All path parameters that are used by at least one ShopperBaskets method.
 */
export type ShopperBasketsPathParameters = {
    organizationId?: string;
    basketId?: string;
    couponItemId?: string;
    giftCertificateItemId?: string;
    itemId?: string;
    paymentInstrumentId?: string;
    shipmentId?: string;
};
/**
 * All query parameters that are used by at least one ShopperBaskets method.
 */
export type ShopperBasketsQueryParameters = {
    taxMode?: string;
    siteId?: string;
    locale?: string;
    overrideExisting?: boolean;
    createDestinationBasket?: boolean;
    productItemMergeMode?: string;
    useAsShipping?: boolean;
    removeExternalTax?: boolean;
    useAsBilling?: boolean;
};

export interface BaseUriParameters {
    shortCode: string;
    version?: string; // Optional, will default to "v1" if not provided.
}

/**
 * All parameters that are used by ShopperBaskets.
 */
export type ShopperBasketsParameters = ShopperBasketsPathParameters &
    BaseUriParameters &
    ShopperBasketsQueryParameters;

export type CustomerAddress = {
    address1?: string;
    address2?: string;
    addressId: string;
    city?: string;
    companyName?: string;
    countryCode: string;
    creationDate?: any;
    firstName?: string;
    fullName?: string;
    jobTitle?: string;
    lastModified?: any;
    lastName: string;
    phone?: string;
    postBox?: string;
    postalCode?: string;
    preferred?: boolean;
    salutation?: string;
    secondName?: string;
    stateCode?: string;
    suffix?: string;
    suite?: string;
    title?: string;
} & {
    [key: string]: any;
};
export type CustomerProductListResult = {
    limit: number;
    data: Array<CustomerProductList>;
    total: number;
} & {
    [key: string]: any;
};
export type OptionValue = {
    default?: boolean;
    id: string;
    name?: string;
    price?: number;
} & {
    [key: string]: any;
};
export type Master = {
    masterId: string;
    orderable?: boolean;
    price?: number;
    priceMax?: number;
    prices?: {} & {
        [key: string]: any;
    };
} & {
    [key: string]: any;
};
export type PublicProductListResult = {
    limit: number;
    data: Array<PublicProductListInfo>;
    total: number;
} & {
    [key: string]: any;
};
export type BasketsResult$0 = {
    baskets: Array<Basket>;
    total: number;
} & {
    [key: string]: any;
};
export type TrustedSystemAuthRequest = {
    clientId: string;
    login: string;
} & {
    [key: string]: any;
};
export type ResetPasswordRequest = {
    resetToken: string;
    login: string;
    newPassword: string;
} & {
    [key: string]: any;
};
export type PaymentCard$0 = {
    cardType: string;
    creditCardExpired?: boolean;
    creditCardToken?: string;
    expirationMonth?: number;
    expirationYear?: number;
    holder?: string;
    issueNumber?: string;
    maskedNumber?: string;
    numberLastDigits?: string;
    validFromMonth?: number;
    validFromYear?: number;
} & {
    [key: string]: any;
};
export type Discount$0 = {
    amount?: number;
    percentage?: number;
    priceBookId?: string;
    type: string;
} & {
    [key: string]: any;
};
export type ResetPasswordToken = {
    login: string;
    email: string;
    resetToken: string;
    expiresInMinutes: number;
} & {
    [key: string]: any;
};
export type ShippingPromotion$0 = {
    calloutMsg: string;
    promotionId: string;
    promotionName: string;
} & {
    [key: string]: any;
};
export type VariationAttribute = {
    id: string;
    name?: string;
    values?: Array<VariationAttributeValue>;
} & {
    [key: string]: any;
};
export type ProductListShippingAddress = {
    addressId: string;
    city?: string;
    firstName?: string;
    lastName?: string;
} & {
    [key: string]: any;
};
export type ProductItem$0 = {
    adjustedTax?: number;
    basePrice?: number;
    bonusDiscountLineItemId?: string;
    bonusProductLineItem?: boolean;
    bundledProductItems?: Array<ProductItem$0>;
    gift?: boolean;
    giftMessage?: string;
    inventoryId?: string;
    itemId?: string;
    itemText?: string;
    optionItems?: Array<OptionItem>;
    price?: number;
    priceAdjustments?: Array<PriceAdjustment>;
    priceAfterItemDiscount?: number;
    priceAfterOrderDiscount?: number;
    productId?: string;
    productListItem?: ProductListItemReference;
    productName?: string;
    quantity: number;
    shipmentId?: string;
    shippingItemId?: string;
    tax?: number;
    taxBasis?: number;
    taxClassId?: string;
    taxRate?: number;
} & {
    [key: string]: any;
};
export type CustomerProductListRegistrant = {
    email: string;
    firstName: string;
    lastName: string;
    role: string;
} & {
    [key: string]: any;
};
export type PublicProductListInfo = {
    description?: string;
    id: string;
    name?: string;
    title: string;
    type: string;
} & {
    [key: string]: any;
};
export type CustomerPaymentCardRequest = {
    cardType: string;
    creditCardToken?: string;
    expirationMonth: number;
    expirationYear: number;
    holder: string;
    issueNumber: string;
    number: string;
    validFromMonth: number;
    validFromYear: number;
} & {
    [key: string]: any;
};
export type RecommendationType = {
    displayValue: string;
    value: number;
} & {
    [key: string]: any;
};
export type ProductType = {
    bundle: boolean;
    item: boolean;
    master: boolean;
    option: boolean;
    set: boolean;
    variant: boolean;
    variationGroup: boolean;
} & {
    [key: string]: any;
};
export type PaymentBankAccount = {
    driversLicenseLastDigits?: string;
    driversLicenseStateCode?: string;
    holder?: string;
    maskedDriversLicense?: string;
    maskedNumber?: string;
    numberLastDigits?: string;
} & {
    [key: string]: any;
};
export type Recommendation = {
    calloutMsg?: string;
    image?: Image;
    longDescription?: string;
    name?: string;
    recommendationType: RecommendationType;
    recommendedItemId?: string;
    recommendedItemLink?: string;
    shortDescription?: string;
} & {
    [key: string]: any;
};
/**
 * @deprecated Prefer `SalesforceProductVariant` from `~/lib/salesforce/types`, which types the
 * variant Salesforce actually returns instead of leaning on an index signature.
 * See TODO(IMC-1671).
 */
export type Variant = {
    orderable?: boolean;
    price?: number;
    productId: string;
    variationValues?: {} & {
        [key: string]: any;
    };
} & {
    [key: string]: any;
};
export type CustomerPaymentInstrument = {
    bankRoutingNumber?: string;
    creationDate?: any;
    lastModified?: any;
    maskedGiftCertificateCode?: string;
    paymentBankAccount: PaymentBankAccount;
    paymentCard: PaymentCard$0;
    paymentInstrumentId: string;
    paymentMethodId: string;
} & {
    [key: string]: any;
};
type VariationGroup = {
    orderable: boolean;
    price: number;
    productId: string;
    variationValues: {} & {
        [key: string]: any;
    };
} & {
    [key: string]: any;
};
export type PaymentBankAccountRequest = {
    driversLicense: string;
    driversLicenseStateCode: string;
    holder: string;
    number: string;
} & {
    [key: string]: any;
};
export type PriceAdjustment$0 = {
    appliedDiscount: Discount$0;
    couponCode: string;
    createdBy: string;
    creationDate: any;
    custom: boolean;
    itemText: string;
    lastModified: any;
    manual: boolean;
    price: number;
    priceAdjustmentId: string;
    promotionId: string;
    promotionLink: string;
    reasonCode: string;
} & {
    [key: string]: any;
};
export type PublicProductListItem = {
    id: string;
    priority: number;
    product?: Product;
    type: string;
} & {
    [key: string]: any;
};
export type ProductDetailsLink$0 = {
    productDescription?: string;
    productId: string;
    productName?: string;
    title?: string;
} & {
    [key: string]: any;
};
export type ProductLink = {
    sourceProductId: string;
    sourceProductLink: string;
    targetProductId: string;
    targetProductLink: string;
    type: string;
} & {
    [key: string]: any;
};
export type OptionItem$0 = {
    adjustedTax?: number;
    basePrice?: number;
    bonusDiscountLineItemId?: string;
    bonusProductLineItem?: boolean;
    bundledProductItems?: Array<ProductItem$0>;
    gift?: boolean;
    giftMessage?: string;
    inventoryId?: string;
    itemId?: string;
    itemText?: string;
    optionId: string;
    optionItems?: Array<OptionItem$0>;
    optionValueId: string;
    price?: number;
    priceAdjustments?: Array<PriceAdjustment$0>;
    priceAfterItemDiscount?: number;
    priceAfterOrderDiscount?: number;
    productId?: string;
    productListItem?: ProductListItemReference;
    productName?: string;
    quantity?: number;
    shipmentId?: string;
    shippingItemId?: string;
    tax?: number;
    taxBasis?: number;
    taxClassId?: string;
    taxRate?: number;
} & {
    [key: string]: any;
};
export type ShippingMethod$0 = {
    description?: string;
    externalShippingMethod?: string;
    id: string;
    name?: string;
    price?: number;
    shippingPromotions?: Array<ShippingPromotion$0>;
} & {
    [key: string]: any;
};
export type ProductListEvent = {
    city?: string;
    country?: string;
    date?: any;
    state?: string;
    type: string;
} & {
    [key: string]: any;
};
export type PasswordChangeRequest = {
    currentPassword: string;
    password: string;
} & {
    [key: string]: any;
};
export type AuthRequest = {
    type?: string;
} & {
    [key: string]: any;
};
export type Inventory = {
    ats?: number;
    backorderable?: boolean;
    id: string;
    inStockDate?: any;
    orderable?: boolean;
    preorderable?: boolean;
    stockLevel?: number;
} & {
    [key: string]: any;
};
export type CustomerProductList = {
    coRegistrant?: CustomerProductListRegistrant;
    creationDate?: any;
    currentShippingAddressInfo?: CustomerAddressInfo;
    customerProductListItems?: Array<CustomerProductListItem>;
    description?: string;
    event?: ProductListEvent;
    id?: string;
    lastModified?: any;
    name?: string;
    postEventShippingAddressInfo?: CustomerAddressInfo;
    productListShippingAddress?: ProductListShippingAddress;
    public?: boolean;
    registrant?: CustomerProductListRegistrant;
    shippingAddressInfo?: CustomerAddressInfo;
    type?: string;
} & {
    [key: string]: any;
};
export type CustomerPaymentInstrumentRequest = {
    bankRoutingNumber: string;
    giftCertificateCode: string;
    paymentBankAccount?: PaymentBankAccountRequest;
    paymentCard: CustomerPaymentCardRequest;
    paymentMethodId: string;
} & {
    [key: string]: any;
};
export type ImageGroup = {
    images: Array<Image>;
    variationAttributes?: Array<VariationAttribute>;
    viewType: string;
} & {
    [key: string]: any;
};
export type CustomerRegistration = {
    customer: Customer;
    password: string;
} & {
    [key: string]: any;
};
export type Customer = {
    addresses?: Array<CustomerAddress>;
    authType?: string;
    birthday?: any;
    companyName?: string;
    creationDate?: any;
    customerId?: string;
    customerNo?: string;
    email?: string;
    enabled?: boolean;
    fax?: string;
    firstName?: string;
    gender?: number;
    jobTitle?: string;
    lastLoginTime?: any;
    lastModified?: any;
    lastName?: string;
    lastVisitTime?: any;
    login?: string;
    note?: string;
    paymentInstruments?: Array<CustomerPaymentInstrument>;
    phoneBusiness?: string;
    phoneHome?: string;
    phoneMobile?: string;
    preferredLocale?: string;
    previousLoginTime?: any;
    previousVisitTime?: any;
    salutation?: string;
    secondName?: string;
    suffix?: string;
    title?: string;
} & {
    c_acceptedTermsAndConditions?: boolean;
    c_externallyAuthenticated?: boolean;
    c_isMemberPlus?: boolean;
    c_newsletter?: boolean;
    c_socialMedia?: boolean;
    c_storeZip?: string;
    c_preferredStore?: string;
    c_imercoCustomerNo?: string;
    c_bonusAvailablePoints?: number;
    [key: string]: any;
};
export type PublicProductList = {
    coRegistrant?: ProductListRegistrant;
    creationDate?: any;
    description?: string;
    event?: ProductListEvent;
    id: string;
    lastModified?: any;
    name?: string;
    productListItems?: Array<PublicProductListItem>;
    productListShippingAddress?: ProductListShippingAddress;
    public: boolean;
    registrant?: ProductListRegistrant;
    type: string;
} & {
    [key: string]: any;
};
export type Option = {
    description?: string;
    id: string;
    image?: string;
    name?: string;
    values?: Array<OptionValue>;
} & {
    [key: string]: any;
};
export type CustomerOrderResult = {
    limit: number;
    data: Array<Order>;
    offset: number;
    total: number;
} & {
    [key: string]: any;
};
export type CustomerExtProfileRequest = {
    authenticationProviderId: string;
    email?: string;
    externalId: string;
    firstName?: string;
    lastName?: string;
} & {
    [key: string]: any;
};
export type GiftCertificateItem$0 = {
    amount: number;
    giftCertificateItemId?: string;
    message?: string;
    recipientEmail: string;
    recipientName?: string;
    senderName?: string;
    shipmentId?: string;
} & {
    [key: string]: any;
};
export type CustomerExternalProfile = {
    customerId: string;
    authenticationProviderId: string;
    email?: string;
    externalId: string;
    firstName?: string;
    lastName?: string;
} & {
    [key: string]: any;
};
export type CustomerAddressInfo = {
    addressId: string;
    title: string;
} & {
    [key: string]: any;
};
export type ResetPasswordTokenRequest = {
    login: string;
} & {
    [key: string]: any;
};
export type ProductListLink$0 = {
    description: string;
    link: string;
    name: string;
    public: boolean;
    title: string;
    type: string;
} & {
    [key: string]: any;
};
export type ProductListRegistrant = {
    firstName: string;
    lastName: string;
    role: string;
} & {
    [key: string]: any;
};

/**
 * @deprecated Prefer `SalesforceProductSpecialPrice` from `~/lib/salesforce/types`. This one
 * declares the money fields as strings; Salesforce sends numbers. See TODO(IMC-1671).
 */
export type SpecialPrices = {
    id?: string;
    value?: string;
    label?: string;
    promotype?: string;
    retailPrice?: string;
    discountAmount?: string;
    effectivePrice?: string;
    splash?: string;
    basketAmountThreshold?: number;
    splashBelowThreshold?: string;
    splashAboveThreshold?: string;
    amountInBasket?: string;
    staticTileFooterText?: string;
    tileFooterText?: string;
    basketNumberOfProductsThreshold?: number;
    isAmountThreshold?: boolean;
    quantityInBasket?: number;
    campaignElements?: string[];
};

export type CTAButtonState = {
    translationKey: string;
    isDisabled: boolean;
    shouldRender: boolean;
};

export type BuyButtonsObj = {
    c_primaryButton: CTAButtonState;
    c_secondaryButton: CTAButtonState;
};

/**
 * @deprecated Prefer `SalesforceProduct` from `~/lib/salesforce/types`, which describes the
 * OCAPI product with its Imerco custom attributes. See TODO(IMC-1671).
 */
export type Product = {
    brand?: string;
    bundledProducts?: Array<BundledProduct>;
    currency?: string;
    ean?: string;
    fetchDate?: number;
    id: string;
    imageGroups?: Array<ImageGroup>;
    inventories?: Array<Inventory>;
    inventory?: Inventory;
    longDescription?: string;
    manufacturerName?: string;
    manufacturerSku?: string;
    master?: Master;
    minOrderQuantity?: number;
    name?: string;
    options?: Array<Option>;
    pageDescription?: string;
    pageKeywords?: string;
    pageTitle?: string;
    price?: number;
    pricePerUnit?: number;
    priceMax?: number;
    prices?: {} & {
        [key: string]: any;
    };
    primaryCategoryId?: string;
    productLinks?: Array<ProductLink>;
    productPromotions?: Array<ProductPromotion>;
    recommendations?: Array<Recommendation>;
    setProducts?: Array<Product>;
    shortDescription?: string;
    stepQuantity?: number;
    type?: ProductType;
    unit?: string;
    upc?: string;
    validFrom?: any;
    validTo?: any;
    variants?: Array<Variant>;
    c_variants?: Array<Variant>;
    c_yotpoAverageRating?: number;
    c_yotpoReviewCount?: number;
    c_minumumPrice?: number;
    variationAttributes?: Array<VariationAttribute>;
    variationGroups?: Array<VariationGroup>;
    variationValues?: {} & {
        [key: string]: any;
    };
    c_ProductUsp?: string;
    c_activepromo?: {
        promotionid?: string;
        promotiontype?: string;
        type?: string;
    };
    c_personalization?: boolean;
    c_personalization_details?: {
        description2: string;
        itemId: string;
        metadata: string;
        price: string;
        basePrice: string;
        productID: string;
    };
    c_personalization_metadata?: string;
    c_prices?: TPrice[];
    c_specialPrices?: SpecialPrices;
    c_isRemainder?: boolean;
    c_burdFlag?: boolean;
    c_ItemHeader?: string;
    c_prelaunch?: string | null;
    c_buyButtonsObj?: BuyButtonsObj;
    c_displayUntilDate?: string;
    c_isDisplayUntilDate?: boolean;
    c_storeIdsWithStock?: string[];
    /** Promotions the product will be discounted by once their countdown ends. */
    c_promotionCountdown?: SalesforcePromotionCountdown[];
} & {
    [key: string]: any;
};
export type Image = {
    alt?: string;
    disBaseLink?: string;
    link: string;
    title?: string;
} & {
    [key: string]: any;
};
export type VariationAttributeValue = {
    description?: string;
    image?: Image;
    imageSwatch?: Image;
    name?: string;
    orderable?: boolean;
    value: string;
} & {
    [key: string]: any;
};
export type ProductPromotion = {
    calloutMsg: string;
    promotionId: string;
    promotionalPrice: number;
} & {
    [key: string]: any;
};
export type BundledProduct = {
    id: string;
    product: Product;
    quantity: number;
} & {
    [key: string]: any;
};
export type Status$0 = {
    code: string;
    message: string;
    status: number;
} & {
    [key: string]: any;
};
export type CustomerProductListItem = {
    id?: string;
    priority: number;
    product?: Product;
    productId?: string;
    public: boolean;
    purchasedQuantity?: number;
    quantity: number;
    type?: string;
} & {
    [key: string]: any;
};
export type ProductListItemReference$0 = {
    id: string;
    priority?: number;
    productList?: ProductListLink$0;
    public?: boolean;
    purchasedQuantity?: number;
    quantity?: number;
    type?: string;
} & {
    [key: string]: any;
};
export type TermQuery$0 = {
    fields: Array<string>;
    operator: string;
    values?: Array<any>;
} & {
    [key: string]: any;
};
export type TextQuery$0 = {
    fields: Array<string>;
    searchPhrase: string;
} & {
    [key: string]: any;
};
export type BoolQuery$0 = {
    must?: Array<any>;
    mustNot?: Array<any>;
    should?: Array<any>;
} & {
    [key: string]: any;
};
export type NestedQuery$0 = {
    path: string;
    query: any;
    scoreMode?: string;
} & {
    [key: string]: any;
};
/**
 * All path parameters that are used by at least one ShopperCustomers method.
 */
export type ShopperCustomersPathParameters = {
    organizationId?: string;
    customerId?: string;
    addressName?: string;
    paymentInstrumentId?: string;
    listId?: string;
    itemId?: string;
};
/**
 * All query parameters that are used by at least one ShopperCustomers method.
 */
export type ShopperCustomersQueryParameters = {
    siteId?: string;
    clientId?: string;
    externalId?: string;
    authenticationProviderId?: string;
    crossSites?: boolean;
    from?: string;
    until?: string;
    status?: string;
    offset?: any;
    limit?: number;
    email?: string;
    firstName?: string;
    lastName?: string;
};
/**
 * All parameters that are used by ShopperCustomers.
 */
export type ShopperCustomersParameters = ShopperCustomersPathParameters &
    BaseUriParameters &
    ShopperCustomersQueryParameters;

type BrandSuggestions = {} & {
    [key: string]: any;
};

type SuggestedCategory = {
    id: string;
    name: string;
    parentCategoryName: string;
} & {
    [key: string]: any;
};

type Image$0 = {
    alt?: string;
    disBaseLink?: string;
    link: string;
    title?: string;
} & {
    [key: string]: any;
};

type SuggestedProduct = {
    currency: string;
    image?: Image$0;
    price: number;
    productId: string;
    productName: string;
} & {
    [key: string]: any;
};

type SuggestedPhrase = {
    exactMatch: boolean;
    phrase: string;
} & {
    [key: string]: any;
};

type SuggestedTerm = {
    completed: boolean;
    corrected: boolean;
    exactMatch: boolean;
    value: string;
} & {
    [key: string]: any;
};

type SuggestedTerms = {
    originalTerm: string;
    terms?: Array<SuggestedTerm>;
} & {
    [key: string]: any;
};

type ProductSuggestions = {
    products?: Array<SuggestedProduct>;
    suggestedPhrases?: Array<SuggestedPhrase>;
    suggestedTerms: Array<SuggestedTerms>;
} & {
    [key: string]: any;
};

type CategorySuggestions = {
    categories?: Array<SuggestedCategory>;
    suggestedPhrases?: Array<SuggestedPhrase>;
    suggestedTerms: Array<SuggestedTerms>;
} & {
    [key: string]: any;
};

type CustomSuggestions = {
    customSuggestion?: string;
    suggestedPhrases?: Array<SuggestedPhrase>;
    suggestedTerms: Array<SuggestedTerms>;
} & {
    [key: string]: any;
};

export type SuggestionResult = {
    brandSuggestions?: BrandSuggestions;
    categorySuggestions?: CategorySuggestions;
    productSuggestions?: ProductSuggestions;
    customSuggestion?: CustomSuggestions;
    searchPhrase: string;
} & {
    [key: string]: any;
};

export type ProductResult = {
    limit: number;
    data: Array<Product>;
    total: number;
} & {
    [key: string]: any;
};

export type GiftCertificateBalance = {
    balance: number;
    certificateNumber: string;
    expirationDate: string;
    isValid: boolean;
};

export type ProductItem$1 = {
    adjustedTax?: number;
    basePrice?: number;
    bonusDiscountLineItemId?: string;
    bonusProductLineItem?: boolean;
    bundledProductItems?: Array<ProductItem$1>;
    gift?: boolean;
    giftMessage?: string;
    inventoryId?: string;
    itemId?: string;
    itemText?: string;
    optionItems?: Array<OptionItem$0>;
    price?: number;
    priceAdjustments?: Array<PriceAdjustment$0>;
    priceAfterItemDiscount?: number;
    priceAfterOrderDiscount?: number;
    productId?: string;
    productListItem?: ProductListItemReference$0;
    productName?: string;
    quantity?: number;
    shipmentId?: string;
    shippingItemId?: string;
    tax?: number;
    taxBasis?: number;
    taxClassId?: string;
    taxRate?: number;
} & {
    [key: string]: any;
};

export interface SalesforceBMAuthResponse {
    access_token: string;
    expires_in: number;
    token_type: string;
}

/**
 * @deprecated Prefer `SalesforceProductPromotionType` from `~/lib/salesforce/types`, which this
 * now simply renames. It used to be a second declaration with identical members, nominally
 * distinct, so the two never unified. Kept only so existing imports keep resolving.
 *
 * TODO(IMC-1671): Resolve imports to `~/lib/salesforce/types/product` instead of `/data-contract/salesforce`.
 */
export { SalesforceProductPromotionType as PromotionType } from '~/lib/salesforce/types/price';
